// src/Pages/Patient/VisionCorrection.tsx
import { ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";

/** Backend values, in the order they are offered in the dropdown. */
export const CORRECTION_VALUES = ["none", "clips", "contactlenses", "glasses"] as const;
export type Correction = (typeof CORRECTION_VALUES)[number];
export const CORRECTION_DEFAULT: Correction = "none";

/** Dioptric powers of the clip-ins, in the order requested by the business. */
export const POWER_VALUES = ["-2.00", "-4.00", "-6.00", "-8.00", "0", "+2.00", "+4.00", "+6.00"] as const;
export type Power = (typeof POWER_VALUES)[number];
export const POWER_DEFAULT: Power = "0";

/** State shape shared with Patient.js and the /updateUsedVisionCorrection payload. */
export type VisionCorrectionValue = {
  correction: Correction;
  power_dpt_R: Power;
  power_dpt_L: Power;
};

export const VISION_CORRECTION_DEFAULT: VisionCorrectionValue = {
  correction: CORRECTION_DEFAULT,
  power_dpt_R: POWER_DEFAULT,
  power_dpt_L: POWER_DEFAULT,
};

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null;

/** `includes` on a readonly tuple only accepts its own members, so widen to compare. */
const oneOf = <T extends string>(allowed: readonly T[], v: unknown, fallback: T): T =>
  (allowed as readonly string[]).includes(String(v)) ? (String(v) as T) : fallback;

/**
 * Old measurements carry no vision correction at all, and the backend may deliver it
 * either flat or nested. Anything unknown or empty falls back to the defaults.
 *
 * Called from Patient.js with the untyped measurement, hence the `unknown` parameter.
 */
export const normalizeVisionCorrection = (measurement: unknown): VisionCorrectionValue => {
  const outer = isRecord(measurement) ? measurement : {};
  const raw = isRecord(outer.UsedVisionCorrection) ? outer.UsedVisionCorrection : outer;
  return {
    correction: oneOf(CORRECTION_VALUES, raw.correction ?? raw.Correction, CORRECTION_DEFAULT),
    power_dpt_R: oneOf(POWER_VALUES, raw.power_dpt_R ?? raw.Power_dpt_R, POWER_DEFAULT),
    power_dpt_L: oneOf(POWER_VALUES, raw.power_dpt_L ?? raw.Power_dpt_L, POWER_DEFAULT),
  };
};

type Props = {
  value: VisionCorrectionValue;
  /** Receives the complete next state, so the caller can send it as one payload. */
  onChange: (next: VisionCorrectionValue) => void;
  /**
   * Rendered in the same non-wrapping group as the correction dropdown — the
   * "Save and send" button, which must never wrap away from it.
   */
  leading?: ReactNode;
  disabled?: boolean;
};

/**
 * The vision correction a measurement was taken with — OV/WIDRME only.
 *
 * Lays out the whole control row under the reference name: `leading` (the button)
 * and the correction dropdown form one group that always shares a line, and the two
 * eye powers (clip-ins only) form a second that wraps beneath it as a pair.
 */
const VisionCorrection = ({ value, onChange, leading, disabled = false }: Props) => {
  const { t } = useTranslation();

  const correctionOptions = useMemo(
    () => CORRECTION_VALUES.map((v) => ({ value: v, label: t(`visionCorrection.${v}`) })),
    [t]
  );
  const powerOptions = useMemo(() => POWER_VALUES.map((v) => ({ value: v, label: v })), []);

  const update = (patch: Partial<VisionCorrectionValue>) => onChange({ ...value, ...patch });

  const dropdownProps = {
    className: "mimetika-dropdown border w-full min-w-0",
    panelClassName: "mimetika-dropdown-panel",
    disabled,
  };

  return (
    <div className="filter-container flex flex-wrap items-end grow min-w-0 text-left text-white">
      {/*
        Button and correction dropdown, never split: plain `flex`, no wrap. Sized by
        its content, so the eye pair wraps beneath as soon as it no longer fits.
        No label on the dropdown; the option text ("Measurement with …") names it.
      */}
      <div className="flex items-end min-w-0">
        {leading}
        {/*
          Fixed 20rem, not `grow`: the row gets wider when the eye pair appears, and a
          growing dropdown would resize with it. It may still shrink on narrow screens.
        */}
        <Dropdown
          inputId="vision-correction"
          aria-label={t("visionCorrection.ariaLabel")}
          value={value.correction}
          options={correctionOptions}
          onChange={(e: DropdownChangeEvent) => update({ correction: e.value as Correction })}
          {...dropdownProps}
          className="mimetika-dropdown border min-w-0 basis-80 max-w-full"
        />
      </div>

      {value.correction === "clips" && (
        <div className="flex items-end gap-2 grow basis-48 max-w-24rem min-w-0">
          <div className="flex flex-column grow min-w-0">
            <label className="text-xs opacity-70" htmlFor="vision-correction-right">
              {t("visionCorrection.rightEye")}
            </label>
            <Dropdown
              inputId="vision-correction-right"
              value={value.power_dpt_R}
              options={powerOptions}
              onChange={(e: DropdownChangeEvent) => update({ power_dpt_R: e.value as Power })}
              {...dropdownProps}
            />
          </div>
          <div className="flex flex-column grow min-w-0">
            <label className="text-xs opacity-70" htmlFor="vision-correction-left">
              {t("visionCorrection.leftEye")}
            </label>
            <Dropdown
              inputId="vision-correction-left"
              value={value.power_dpt_L}
              options={powerOptions}
              onChange={(e: DropdownChangeEvent) => update({ power_dpt_L: e.value as Power })}
              {...dropdownProps}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default VisionCorrection;
